// The project-brain state hook (Brief 5) — the single owner of a project's WIKI
// + Signal's async QUESTIONS for the session/channel view. The wiki and the
// answered/pending state are written by the orchestrator's Signal pass; the
// backend fans an `event: brain` frame out on each write (mirroring trace), so
// this hook stays LIVE over the SSE stream — no polling. An initial read fills
// it; a `brain` frame for this project re-reads it (the current data stays on
// screen during the refetch, so a live page never flickers). The wire is
// injected via `useApi()` — this was the one dashboard-coupled hook
// the miniapp pulled in; it now lives in @waytide/ui with the rest.
//
// `pickPending` (which question to surface) is pure, so it is unit-tested
// without a network.

import { useEffect, useRef, useState } from "react";
import type { BrainQuestion, WikiSection } from "@waytide/core";
import { useApi } from "../api";

/** The single open question to surface — the oldest still "pending" (Signal
 * asks one at a time, so there is normally at most one; oldest-first is
 * defensive). Pure. */
export function pickPending(questions: BrainQuestion[]): BrainQuestion | null {
  return (
    questions
      .filter((q) => q.status === "pending")
      .sort((a, b) => a.created_at - b.created_at)[0] ?? null
  );
}

export interface ProjectBrain {
  sections: WikiSection[];
  /** All questions (for per-section state: asked / answered / unasked). */
  questions: BrainQuestion[];
  /** The single open question, or null. */
  pending: BrainQuestion | null;
  /** True only while the FIRST load for the current project is in flight. */
  loading: boolean;
}

/** Load + keep-live a project's wiki + questions over the `brain` SSE frame. A
 * null/0 project id is IDLE (no fetch) — the session has no project yet (Signal
 * hasn't created it). */
export function useProjectBrain(projectId: number | null): ProjectBrain {
  const api = useApi();
  const [sections, setSections] = useState<WikiSection[]>([]);
  const [questions, setQuestions] = useState<BrainQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  // The project the current data belongs to — a CHANGE clears it (never show
  // project A's brain under project B); a same-project refresh keeps it on
  // screen for a silent re-render.
  const shownId = useRef<number | null>(null);

  useEffect(() => {
    if (projectId == null || projectId === 0) {
      shownId.current = null;
      setSections([]);
      setQuestions([]);
      setLoading(false);
      return;
    }
    const isNew = shownId.current !== projectId;
    shownId.current = projectId;
    if (isNew) {
      setSections([]);
      setQuestions([]);
      setLoading(true);
    }

    let alive = true;
    const pull = () => {
      void api
        .getProjectWiki(projectId)
        .then((r) => {
          if (alive) setSections(r.sections);
        })
        .catch(() => {})
        .finally(() => {
          if (alive) setLoading(false);
        });
      void api
        .getProjectQuestions(projectId)
        .then((r) => {
          if (alive) setQuestions(r.questions);
        })
        .catch(() => {});
    };
    pull();
    // Live: re-read when Signal changes THIS project's brain. The backend fans a
    // `brain` frame on each wiki/question write (no polling).
    const unsub = api.subscribeChat(
      () => {},
      () => {},
      undefined,
      undefined,
      undefined,
      (b) => {
        if (alive && b.project_id === projectId) pull();
      },
    );
    return () => {
      alive = false;
      unsub();
    };
  }, [api, projectId]);

  return {
    sections,
    questions,
    pending: pickPending(questions),
    loading,
  };
}
